import { FileUpload } from '@/components/atoms'
import { useCSVFileValidation } from '@/hooks/useCSVFileValidation-hook'
import { useFileUpload } from '@/hooks/useFileUpload'
import { useValidateProductIds } from '@/hooks/useValidateProductIds-hook'
import { useAppDispatch } from '@/store'
import { setStoreListUrl } from '@/store/slices/salesTargetSlice'
import { Constants, NUMBERS } from '@/utils/constants/commonConstants'
import Papa from 'papaparse'
import { useState } from 'react'
import './upload-file.scss'
import { ROUTES } from '@/utils/constants/routesConstants'
import getUploadFileHelperText from './getUploadFileHelperText'

const ERROR_HEADER = Constants.CorruptedFileTitle
const ERROR_MESSAGE = Constants.CorruptedFileMessage

/**
 * UploadFile component allows users to upload files with various configurations and validations.
 *
 * @param {Object} props - The properties object.
 * @param {string[]} props.acceptedExtensions - List of accepted file extensions.
 * @param {string} props.description - Description for the file upload.
 * @param {string} props.label - Label for the file upload input.
 * @param {File[]} props.files - Array of files to be uploaded.
 * @param {boolean} [props.multiple=false] - Allow multiple file uploads.
 * @param {string} [props.className=''] - Additional class names for the component.
 * @param {string} [props.containerClassName=''] - Additional class names for the container.
 * @param {boolean} [props.required=false] - Whether the file upload is required.
 * @param {string} props.variant - Variant of the file upload component.
 * @param {string} [props.buttonLabel=Constants.UploadFile] - Label for the upload button.
 * @param {string} props.type - Type of the file upload.
 * @param {string} [props.imageUri=''] - URI of the image to be uploaded.
 * @param {Function} [props.setFile=() => {}] - Function to set the file.
 * @param {Function} [props.uploadFileSuccess=() => {}] - Function to handle successful file upload.
 * @param {Function} props.resetUploadFile - Function to reset the file upload.
 * @param {boolean} [props.isDark=true] - Whether the component is in dark mode.
 * @param {Function} [props.onUpload=() => {}] - Function to handle file upload.
 * @param {string} props.feedbackType - Type of feedback to be displayed.
 * @param {Function} [props.onUploadDelete=() => {}] - Function to handle file upload deletion.
 * @param {string} [props.helperText=''] - Helper text to be displayed.
 * @param {Function} [props.onUploadError=() => {}] - Function to handle file upload errors.
 * @param {boolean} [props.fileSizeCheck=false] - Whether to check the file size.
 * @param {string} [props.dataTestId=''] - Data test ID for the component.
 * @param {boolean} [props.disabled=false] - Whether the file upload is disabled.
 * @param {Function} [props.onSetError=() => {}] - Function to handle file upload errors and to handle upload modal button.
 * @param {Function} [props.setFileErrorMessage=() => {}] - Function to handle file upload errors and to handle upload modal button.
 * @param {boolean} [props.isValidateProductIds=false] - Whether to validate product IDs.
 * @param {Function} [props.validateProductIdsCallback=() => {}] - Function to validate product IDs.
 *
 *
 * @returns {JSX.Element} The UploadFile component.
 */
const UploadFile = ({
  acceptedExtensions,
  description,
  label,
  files,
  multiple = false,
  className = '',
  containerClassName = '',
  variant,
  buttonLabel = Constants.UploadFile,
  type,
  imageUri = '',
  setFile = () => { },
  uploadFileSuccess = () => { },
  resetUploadFile,
  isDark = true,
  onUpload = () => { },
  feedbackType,
  onUploadDelete = () => { },
  helperText = '',
  onUploadError = () => { },
  fileSizeCheck = false,
  dataTestId = '',
  disabled = false,
  uploadFor,
  onSetError = () => { },
  getFileUrl,
  // setSecondaryBtnText = () => {},
  isCsvValidationRequired = false,
  required = true,
  setFileErrorMessage = () => { },
  fileType = '',
  customRules = null,
  isValidateProductIds = false,
  validateProductIdsCallback = () => { },
  uploading,
  setUploading,
  customErrorMessages = {},
}) => {
  const { uploadFile, progress } = useFileUpload()
  const { onValidateProductIds, validateProductIdsProgress } = useValidateProductIds()

  const { validateCSVFile } = useCSVFileValidation()
  const [error, setError] = useState(false)
  const [fileError, setFileError] = useState({})
  const dispatch = useAppDispatch()
  const getBase64 = (file, url) => {
    // Only proceed if uploadFileSuccess is provided
    if (!uploadFileSuccess) return

    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = function () {
      try {
        dispatch(
          uploadFileSuccess({
            title: file.name,
            imageBase64: reader.result,
            imageUri: URL.createObjectURL(file),
            imageUrl: url,
          })
        )
      } catch (error) {
        console.error('File upload error:', error)
        setFileError({
          feedbackType: Constants.Error,
          helperText: 'Failed to process file',
        })
        onSetError(true)
      }
    }
  }

  const validateImage = file => {
    return new Promise((resolve, reject) => {
      if (!file.type.startsWith('image/')) {
        resolve(true) // Skip validation for non-images
        return
      }

      const img = new Image()
      const url = URL.createObjectURL(file)

      img.onload = () => {
        URL.revokeObjectURL(url)
        resolve(true)
      }

      img.onerror = () => {
        URL.revokeObjectURL(url)
        reject(new Error(`${ERROR_HEADER.trim()}\n${ERROR_MESSAGE}`))
      }

      img.src = url
    })
  }

  //when api is available we can config some changes as per our requirement
  const fileUpload = async file => {
    try {
      // Validate image first
      if (file.type.startsWith('image/')) {
        await validateImage(file)
      }

      const response = await uploadFile(file, uploadFor, fileType)
      if (!response) throw new Error('No response received from upload')

      const { message, media } = response

      // Helper to handle success
      const handleSuccess = (url, isCsv = false) => {
        if (getFileUrl && isCsv) {
          getFileUrl(url)
        }
        isCsv && dispatch(setStoreListUrl(url))
        onUpload(file)
        setFile([file])
        setUploading(false)
        onSetError(false)
        setFileErrorMessage({ feedbackType: '', helperText: '' })
      }

      // CSV upload
      if (file.type === 'text/csv') {
        const resolvedFileUrl = media?.imageUrl || response?.file?.fileUrl
        if (message === Constants.Success && resolvedFileUrl) {
          handleSuccess(resolvedFileUrl, true)
          return
        }
      }
      // Media upload
      else if (message === Constants.Success && media?.imageUrl) {
        getBase64(file, media.imageUrl)
        handleSuccess(media.imageUrl)
        return
      }

      // Error handling
      if (media?.error) throw new Error(media.error)
      throw new Error(Constants.ErrorForFileUpload)
    } catch (err) {
      const errorMessage = err?.message || 'File upload failed'
      if (errorMessage.includes('\n')) {
        const [errorHeader, errorDetails] = errorMessage.split('\n')
        setFileError({
          feedbackType: Constants.Error,
          helperText: (
            <div className="flex flex-col space-y-1">
              <span className="font-semibold">{errorHeader}</span>
              <span>{errorDetails}</span>
            </div>
          ),
        })
      } else {
        setFileError({
          feedbackType: Constants.Error,
          helperText: errorMessage,
        })
      }
      onSetError(true)
    } finally {
      setUploading(false)
    }
  }

  const handleDelete = () => {
    setError(false)
    setFileError({})
    onSetError(false)
    if (resetUploadFile) {
      dispatch(resetUploadFile())
    }
    setFile([])
    onUploadDelete()
  }
  const rules = {
    type: 'text/csv',
    size: '25600',
    columns: [
      {
        name: 'Product Id',
        required: true,
        type: 'string',
        minChar: 1,
        maxChar: 255,
      },
    ],
    requiredHeaders: ['Product Id'], // Add this new field
  }

  const handleFileValidationErrors = (isInvalidFileData, errorsList) => {
    if (isInvalidFileData) {
      setFileError({
        feedbackType: Constants.Error,
        helperText: errorsList.join(', '),
      })
      setUploading(false)
      onSetError(true)
    }
  }

  const handleCsvUpload = file => {
    Papa.parse(file, {
      complete: result => {
        const rows = result?.data
        let validationResult
        if (!customRules) {
          validationResult = validateCSVFile(
            rules,
            { fileType: file?.type, fileSize: file?.size / 1024, csvData: rows },
            'Product Id'
          )
        } else {
          validationResult = validateCSVFile(customRules, {
              fileType: file?.type,
              fileSize: file?.size / 1024,
              csvData: rows,
          }, 'Product ID', ROUTES.POINT_MULTIPLIER_PRODUCTS)
        }

        const { isInvalidFileData, errorsList } = validationResult

        if (isInvalidFileData) {
          handleFileValidationErrors(isInvalidFileData, errorsList)
          return
        }

        if (isValidateProductIds) {
          let key = 'Product Id';
          if (customRules) {
            key = customRules.columns[0]?.name || 'Product Id';
          }
          const productIds = rows.map(row => row[key]).filter(Boolean)
          if (productIds.length > 0) {
            setUploading(true)

            onValidateProductIds(productIds, res => {
              if (Object.keys(res).length !== 0) {
                validateProductIdsCallback({
                  totalProductIds: productIds.length,
                  data: res['data'],
                  fileName: file.name,
                })
              } else {
                setFileError({
                  feedbackType: Constants.Error,
                  helperText: 'Product ID validation failed',
                })
                onSetError(true)
              }
              setUploading(false)
            })
          }
        } else {
          // Only proceed with upload if validation passes
          setUploading(true)
          fileUpload(file)
        }
      },
      header: true,
    })
    setUploading(false)
  }

  const handleUpload = async file => {
    setError(false)
    setFileError({feedbackType: Constants.Info, helperText: ''})
    onSetError(false)

    // Validate file type for CSV first
    if (file?.type === 'text/csv' && isCsvValidationRequired) {
      handleCsvUpload(file)
      return
    }

    // Check file size before setting upload state
    if (fileSizeCheck) {
      try {
        const fileSize = file.size / NUMBERS.OneThousandTwentyFour
        if (fileSize > NUMBERS.FiveHundred) {
          setFileError({
            feedbackType: 'error',
            helperText: 'File cannot be uploaded. File size should be below 500KB',
          })
          onSetError(true)
          return false
        }
      } catch (err) {
        setFileError({
          feedbackType: 'error',
          helperText: err + '. Supported formats: .jpg,.png',
        })
        onSetError(true)
        return false
      }
    }

    // Only set uploading state if all validations pass
    setUploading(true)
    try {
      if (uploadFor === Constants.SalesTarget) {
        await onUpload(file)
        setUploading(false)
        setFileError({feedbackType: Constants.Info, helperText: ''})
        onSetError(false)
      } else {
        await fileUpload(file)
      }
    } catch (error) {
      setUploading(false)
      setFileError({
        feedbackType: Constants.Error,
        helperText: error?.message || 'File upload failed',
      })
      onSetError(true)
    }
  }

  const handleUploadError = err => {
    setError(true)
    setFileError({
      feedbackType: Constants.Error,
      helperText: err,
    })
    onUploadError(err)
  }

  const getFeedbackType = () => {
    if (Object.keys(fileError).length > NUMBERS.Zero) {
      return fileError?.feedbackType
    }
    return feedbackType
  }

  const isFileError = Object.keys(fileError).length > 0 && fileError.feedbackType === Constants.Error

  return (
    <FileUpload
      data-testid="file-upload-input"
      acceptedExtensions={acceptedExtensions}
      description={description}
      label={label}
      variant={uploading ? Constants.Uploading : variant}
      buttonLabel={buttonLabel}
      onUpload={handleUpload}
      onUploadDelete={handleDelete}
      type={type}
      required={required}
      imageUri={imageUri}
      containerClassName={`${containerClassName} ${error || isFileError ? 'border-2 border-dotted border-red-500' : ''}`}
      className={className}
      multiple={multiple}
      value={isFileError ? '' : files}
      isDark={isDark}
      feedbackType={getFeedbackType()}
      helperText={getUploadFileHelperText({
        variant,
        Constants,
        fileError,
        helperText,
        customErrorMessages
      })}
      onUploadError={handleUploadError}
      dataTestId={dataTestId}
      progressPercentage={isValidateProductIds ? validateProductIdsProgress : progress}
      disabled={disabled}
    />
  )
}

export default UploadFile
